import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Link from 'next/link';

import { screen } from '@/components/common/breakpoints';
import {
  CoreStyling,
  createRipple,
  IconButton,
  RippleAnimation,
} from '@/components/common/Buttons/shared';
import { isAnchor } from '@/components/common/Buttons/utils';
import {
  determineDestination,
  VisuallyHidden,
} from '@/components/common/utils';

import type {
  ButtonProps,
  IconButtonProps,
} from '@/components/common/Buttons/shared';
import type { IconSet } from '@/components/common/Icon';
import type { ReactNode } from 'react';

type IconPosition = 'left' | 'right' | 'none';

type IconButtonExtended = IconButtonProps & {
  children: ReactNode;
  iconPosition: IconPosition;
};

const CorePrimaryButton = ({
  componentTheme,
  iconPosition,
  icon = iconPosition === 'left' ? 'ArrowLeft' : 'ArrowRight',
  iconSize,
  children,
}: IconButtonExtended) => {
  const Contents = styled.span`
    position: relative;
  `;

  return (
    <Contents>
      {iconPosition === 'left' && (
        <IconButton
          componentTheme={componentTheme}
          variant="primary"
          icon={icon}
          iconOnly={false}
          className="icon-background"
          iconSize={iconSize}
        />
      )}
      {children}
      {iconPosition === 'right' && (
        <IconButton
          componentTheme={componentTheme}
          variant="primary"
          icon={icon}
          iconOnly={false}
          className="icon-background"
          iconSize={iconSize}
        />
      )}
    </Contents>
  );
};

type PrimaryButtonType = ButtonProps & {
  icon?: IconSet;
  iconPosition?: IconPosition;
  iconSize?: IconButtonProps['iconSize'];
};

export const PrimaryButton = (props: PrimaryButtonType) => {
  const { componentTheme, iconPosition = 'right', icon, iconSize } = props;

  if (isAnchor(props)) {
    const { 'aria-label': ariaLabel, children, onMouseDown, ...rest } = props;
    const destination =
      typeof props?.href === 'string' && determineDestination(props?.href);

    return (
      <PrimaryBtnLink
        componentTheme={componentTheme}
        iconPosition={iconPosition}
        rel={destination.isExternalLink ? 'external noopener' : null}
        aria-label={ariaLabel ? `${ariaLabel} ${destination.newTabText}` : null}
        target={destination.target}
        onMouseDown={(event) => {
          onMouseDown && onMouseDown(event);
          createRipple(event);
        }}
        {...rest}
      >
        <CorePrimaryButton
          componentTheme={componentTheme}
          icon={icon}
          iconPosition={iconPosition}
          iconSize={iconSize}
        >
          {children}
        </CorePrimaryButton>
        {destination.isExternalLink && !ariaLabel && (
          <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
        )}
      </PrimaryBtnLink>
    );
  } else {
    const { children, onMouseDown, ...rest } = props;
    return (
      <PrimaryBtn
        componentTheme={componentTheme}
        iconPosition={iconPosition}
        onMouseDown={(event) => {
          onMouseDown && onMouseDown(event);
          createRipple(event);
        }}
        {...rest}
      >
        <CorePrimaryButton
          componentTheme={componentTheme}
          icon={icon}
          iconPosition={iconPosition}
          iconSize={iconSize}
        >
          {children}
        </CorePrimaryButton>
      </PrimaryBtn>
    );
  }
};

const PrimaryBtnTheme = (componentTheme) => {
  switch (componentTheme) {
    case 'light':
      return css`
        background: var(--surface-primary);
        border: solid 1px var(--surface-primary);
        color: var(--text-inverse-strong);
        &:hover {
          background: var(--surface-inverse-primary);
          border: solid 1px var(--surface-inverse-primary);

          svg {
            color: var(--icon-action-dark);
          }
        }
        &:focus-visible {
          outline: 2px solid var(--border-action-dark);
          outline-offset: 2px;
        }
      `;
    case 'dark':
      return css`
        background: var(--surface-subtle);
        border: solid 1px var(--surface-subtle);
        color: var(--text-action);
        &:hover {
          background: var(--surface-warm-base);
          color: var(--text-action-dark);
          .icon-background {
            background: var(--surface-inverse-primary);
          }
        }
        &:focus-visible {
          outline: 2px solid var(--decorative-dark-eclipse);
          outline-offset: 2px;
        }
      `;
    default:
      return '';
  }
};

const PrimaryBtnIcon = (iconPosition: IconPosition) => {
  if (iconPosition === 'none') {
    return;
  }

  const padding = `padding-${iconPosition}`;

  return css`
    ${padding}: calc(var(--spacing-x-small) + 32px + 4px);

    ${screen.sm} {
      ${padding}: calc(var(--spacing-small) + 40px + 4px);
    }
    ${screen.md} {
      ${padding}: calc(var(--spacing-medium) + 44px + 6px);
    }

    .icon-background {
      ${iconPosition}: calc(-32px - var(--spacing-x-small));

      ${screen.sm} {
        ${iconPosition}: calc(-40px - var(--spacing-small));
      }

      ${screen.md} {
        ${iconPosition}: calc(-44px - var(--spacing-medium));
      }
    }
  `;
};

const PrimaryBtn = styled.button<{
  componentTheme: 'light' | 'dark';
  iconPosition?: 'left' | 'right' | 'none';
}>`
  ${CoreStyling};
  ${RippleAnimation('primary')};
  ${({ componentTheme, iconPosition }) => css`
    ${PrimaryBtnTheme(componentTheme)}
    ${PrimaryBtnIcon(iconPosition)}
  `}

  .icon-background {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
  cursor: pointer;
`;

const PrimaryBtnLink = styled(Link, { shouldForwardProp: isPropValid })<
  Pick<IconButtonExtended, 'componentTheme' | 'iconPosition'>
>`
  ${CoreStyling};
  ${RippleAnimation('primary')};
  ${({ componentTheme, iconPosition }) => css`
    ${PrimaryBtnTheme(componentTheme)}
    ${PrimaryBtnIcon(iconPosition)}
  `}

  .icon-background {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`;
